import React from 'react';
import { Sparkles, Phone, Mail, MapPin, MessageCircle, Globe } from 'lucide-react';
import { PageSection } from '../../../../types';
import { useSite } from '../../../../context/SiteContext';

interface ContactEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const ContactEditor: React.FC<ContactEditorProps> = ({ section, onUpdateContent }) => {
  const { activeSite } = useSite();
  const content = section.content || {};

  const isCustomSource = Boolean(content.useCustomContactInfo);

  return (
    <div className="space-y-6">
      {/* Title & Copy */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">İletişim Başlıkları</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? 'İLETİŞİME GEÇİN'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            placeholder="Örn: İLETİŞİME GEÇİN"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'Sorularınız ve Teklif Talepleriniz İçin Buradayız'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            placeholder="Başlık..."
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama</label>
          <textarea
            value={content.description ?? ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            rows={2}
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      {/* Global vs Custom Source Switch */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-3.5 h-3.5 text-blue-400" />
            <h4 className="text-xs font-bold text-slate-200">İletişim Bilgileri Kaynağı</h4>
          </div>
          <div className="flex items-center gap-1 bg-slate-900 p-0.5 rounded-lg border border-slate-700 text-[10px] font-bold">
            <button
              type="button"
              onClick={() => onUpdateContent({ useCustomContactInfo: false })}
              className={`px-2.5 py-1 rounded-md transition ${
                !isCustomSource ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Site Genelinden Al
            </button>
            <button
              type="button"
              onClick={() => onUpdateContent({ useCustomContactInfo: true })}
              className={`px-2.5 py-1 rounded-md transition ${
                isCustomSource ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Özel Değerler
            </button>
          </div>
        </div>

        {!isCustomSource ? (
          <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-700/60 space-y-2 text-xs text-slate-300">
            <p className="text-[11px] text-slate-400">
              Bu bölüm, projenizin global ayarlarındaki iletişim bilgilerini otomatik olarak kullanır:
            </p>
            <div className="space-y-1.5 pt-1 font-mono text-[11px]">
              <div className="flex items-center gap-2 text-slate-300">
                <Phone className="w-3 h-3 text-cyan-400" />
                <span>{activeSite?.settings?.phone || 'Telefon belirtilmemiş'}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Mail className="w-3 h-3 text-blue-400" />
                <span>{activeSite?.settings?.email || 'E-posta belirtilmemiş'}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <MapPin className="w-3 h-3 text-rose-400" />
                <span>{activeSite?.settings?.address || 'Adres belirtilmemiş'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3 pt-1">
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Phone className="w-3 h-3 text-cyan-400" />
                <span>Özel Telefon Numarası</span>
              </label>
              <input
                type="text"
                value={content.phone ?? ''}
                onChange={(e) => onUpdateContent({ phone: e.target.value })}
                placeholder="+90 (212) 800 50 00"
                className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Mail className="w-3 h-3 text-blue-400" />
                <span>Özel E-posta Adresi</span>
              </label>
              <input
                type="email"
                value={content.email ?? ''}
                onChange={(e) => onUpdateContent({ email: e.target.value })}
                placeholder="destek@siteniz.com"
                className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-rose-400" />
                <span>Özel Adres Bilgisi</span>
              </label>
              <textarea
                value={content.address ?? ''}
                onChange={(e) => onUpdateContent({ address: e.target.value })}
                rows={2}
                placeholder="Ofis ve lokasyon adresi..."
                className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* Form Button Label */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-2">
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Form Gönderme Buton Metni
        </label>
        <input
          type="text"
          value={content.buttonText ?? 'Mesaj Gönder'}
          onChange={(e) => onUpdateContent({ buttonText: e.target.value })}
          placeholder="Mesaj Gönder"
          className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
        />
      </div>
    </div>
  );
};
